import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronDown,
  Copy,
  FileText,
  Package,
  Image as ImageIcon,
  LayoutTemplate,
  Loader2,
  LockKeyhole,
  MapPin,
  Plus,
  Receipt,
  Reply,
  SendHorizontal,
  ListChecks,
  Bot,
  StickyNote,
  X,
} from "lucide-react";
import { TemplateDialog } from "./TemplateDialog";
import { SendQuoteDialog } from "./SendQuoteDialog";
import { ShareProductDialog } from "./ShareProductDialog";
import { WaTicks } from "./WaParts";
import { EmojiPicker } from "./EmojiPicker";
import { VoiceRecorder } from "./VoiceRecorder";
import { ButtonsDialog } from "./ButtonsDialog";
import type { WaConversationDetail, WaMessage, WaQuickReply } from "@/types/whatsapp";

const MAX_FILE_BYTES = 7 * 1024 * 1024;

export const formatTime = (v: string) =>
  new Date(v).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true }).toLowerCase();

const formatDay = (v: string) => {
  const d = new Date(v);
  if (d.toDateString() === new Date().toDateString()) return "Today";
  if (d.toDateString() === new Date(Date.now() - 86400000).toDateString()) return "Yesterday";
  if (Date.now() - d.getTime() < 6 * 86400000) return d.toLocaleDateString("en-IN", { weekday: "long" });
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export const windowLabel = (closesAt: string | null, open: boolean) => {
  if (!open || !closesAt) return "Reply window closed";
  const hours = Math.max(0, (new Date(closesAt).getTime() - Date.now()) / 3600000);
  return hours >= 1 ? `Reply window: ${Math.floor(hours)}h left` : `Reply window: ${Math.ceil(hours * 60)}m left`;
};

const readAsBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const messageText = (m: WaMessage) => m.body || m.caption || m.file_name || m.message_type;

interface ChatPaneProps {
  conversationId: string;
  /** Bump to reload (parent receives live updates) */
  refreshSignal?: number;
  /** Called after anything that changes the conversation list (send, read) */
  onChanged?: () => void;
  className?: string;
}

/** WhatsApp-style chat: wallpaper, bubbles with tails, ticks, and the input bar */
export function ChatPane({ conversationId, refreshSignal = 0, onChanged, className }: ChatPaneProps) {
  const { error, success } = useToast();
  const { hasPermission } = useAuth();
  const canSend = hasPermission("whatsapp.send");

  const [detail, setDetail] = useState<WaConversationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [older, setOlder] = useState<WaMessage[]>([]);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [noteMode, setNoteMode] = useState(false);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState<WaMessage | null>(null);
  const [sending, setSending] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [buttonsOpen, setButtonsOpen] = useState(false);
  const [quickReplies, setQuickReplies] = useState<WaQuickReply[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const stickToBottom = useRef(true);
  const olderLoaded = useRef(false);
  const scrolledReady = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = unwrapObject<WaConversationDetail>(await api.get(`/api/WhatsApp/Conversations/${conversationId}`));
      setDetail(res);
      if (!olderLoaded.current) setHasMoreOlder(res.has_more);
      if (res.conversation.unread_count > 0) {
        await api.patch(`/api/WhatsApp/Conversations/${conversationId}/Read`).catch(() => undefined);
        onChanged?.();
      }
    } catch (err: any) {
      error("Could not load chat", err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => {
    setLoading(true);
    setDetail(null);
    setOlder([]);
    olderLoaded.current = false;
    scrolledReady.current = false;
    setReplyTo(null);
    setText("");
    setNoteMode(false);
    stickToBottom.current = true;
    load();
  }, [conversationId, load]);

  useEffect(() => {
    if (refreshSignal) load();
  }, [refreshSignal, load]);

  useEffect(() => {
    api
      .get("/api/WhatsApp/QuickReplies")
      .then((res) => setQuickReplies(unwrapArray<WaQuickReply>(res)))
      .catch(() => setQuickReplies([]));
  }, []);

  const messages = useMemo(() => {
    const seen = new Set<string>();
    return [...older, ...(detail?.messages || [])].filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true)));
  }, [older, detail]);

  const byWamid = useMemo(() => new Map(messages.filter((m) => m.wamid).map((m) => [m.wamid!, m])), [messages]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
    if (detail) scrolledReady.current = true;
  }, [messages.length, detail]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (scrolledReady.current && hasMoreOlder && !loadingOlder && el.scrollTop < 48) loadOlder();
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !detail || !hasMoreOlder || loadingOlder || !scrolledReady.current) return;
    if (el.scrollHeight <= el.clientHeight + 8) loadOlder();
  }, [messages.length, hasMoreOlder, loadingOlder, detail]);

  const loadingOlderRef = useRef(false);
  const loadOlder = async () => {
    const first = messages[0];
    if (!first || loadingOlderRef.current || !hasMoreOlder) return;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    const el = scrollRef.current;
    const prevHeight = el?.scrollHeight || 0;
    try {
      const res = unwrapObject<WaConversationDetail>(
        await api.get(`/api/WhatsApp/Conversations/${conversationId}?before=${encodeURIComponent(first.message_at)}`)
      );
      stickToBottom.current = false;
      olderLoaded.current = true;
      setOlder((list) => [...res.messages, ...list]);
      setHasMoreOlder(res.has_more);
      requestAnimationFrame(() => {
        if (el) el.scrollTop = el.scrollHeight - prevHeight;
      });
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  };

  const conv = detail?.conversation;
  const windowOpen = Boolean(conv?.window_open);
  const replyLocked = !noteMode && !windowOpen;

  const send = async (payload: Record<string, any>) => {
    setSending(true);
    stickToBottom.current = true;
    try {
      await api.post(`/api/WhatsApp/Conversations/${conversationId}/Send`, {
        ...payload,
        reply_to_message_id: payload.kind === "note" ? undefined : replyTo?.id,
      });
      setReplyTo(null);
      await load();
      onChanged?.();
      return true;
    } catch (err: any) {
      error("Not sent", err.message);
      return false;
    } finally {
      setSending(false);
    }
  };

  const submitText = async () => {
    const value = text.trim();
    if (!value || sending) return;
    if (await send({ kind: noteMode ? "note" : "text", text: value })) {
      setText("");
      inputRef.current?.focus();
    }
  };

  const onFile = async (file?: File, withCaption = true) => {
    if (!file) return false;
    if (fileRef.current) fileRef.current.value = "";
    if (file.size > MAX_FILE_BYTES) {
      error("File too large", "Files up to 7 MB can be sent");
      return false;
    }
    const content_base64 = await readAsBase64(file);
    const ok = await send({
      kind: "media",
      text: withCaption ? text.trim() || undefined : undefined,
      media: { file_name: file.name, content_type: file.type || "application/octet-stream", content_base64 },
    });
    if (ok && withCaption) setText("");
    return ok;
  };

  const insertEmoji = (emoji: string) => {
    const el = inputRef.current;
    if (!el) return setText((t) => t + emoji);
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = text.slice(0, start) + emoji + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + emoji.length, start + emoji.length);
    });
  };

  const slash = text.startsWith("/") && !text.includes(" ") ? text.slice(1).toLowerCase() : null;
  const suggestions = slash !== null ? quickReplies.filter((q) => q.shortcut.startsWith(slash)).slice(0, 6) : [];

  if (loading && !detail) {
    return (
      <div className={cn("wa wa-wallpaper flex items-center justify-center", className)}>
        <Loader2 className="size-6 animate-spin" style={{ color: "var(--wa-green)" }} />
      </div>
    );
  }
  if (!detail || !conv) return <div className={cn("wa p-6 text-sm", className)}>Conversation not found.</div>;

  // Render list: date chips + bubbles; a tail only on the first bubble of a run from the same side
  let lastDay = "";
  let lastSide = "";

  return (
    <div className={cn("wa flex flex-col min-h-0", className)}>
      <div ref={scrollRef} onScroll={onScroll} className="wa-wallpaper wa-scroll flex-1 min-h-0 overflow-y-auto px-[5%] sm:px-[7%] py-3">
        {loadingOlder && (
          <div className="flex justify-center pb-3">
            <Loader2 className="size-3.5 animate-spin" style={{ color: "var(--wa-muted)" }} />
          </div>
        )}

        <div className="flex justify-center mb-3">
          <div
            className="max-w-md text-center text-[12.5px] leading-snug px-3 py-1.5 rounded-lg flex items-start gap-1.5"
            style={{ background: "var(--wa-system-bg)", color: "var(--wa-system-text)", boxShadow: "var(--wa-shadow)" }}
          >
            <LockKeyhole className="size-3 mt-0.5 shrink-0" />
            <span>Messages are sent through the official WhatsApp Business API from your company number and saved in the CRM.</span>
          </div>
        </div>

        {messages.length === 0 && (
          <div className="py-10 text-center text-[13px]" style={{ color: "var(--wa-muted)" }}>
            No messages yet. {windowOpen ? "Say hello!" : "Start the conversation with an approved template."}
          </div>
        )}

        {messages.map((m) => {
          const day = formatDay(m.message_at);
          const showDay = day !== lastDay;
          lastDay = day;
          const side = m.payload?.system ? "system" : m.direction === "IN" ? "in" : m.direction === "NOTE" ? "note" : "out";
          const firstOfRun = showDay || side !== lastSide;
          lastSide = side;
          return (
            <React.Fragment key={m.id}>
              {showDay && (
                <div className="flex justify-center my-2.5 sticky top-0 z-[1]">
                  <span
                    className="text-[12.5px] px-3 py-1 rounded-lg uppercase tracking-wide"
                    style={{ background: "var(--wa-chip)", color: "var(--wa-muted)", boxShadow: "var(--wa-shadow)" }}
                  >
                    {day}
                  </span>
                </div>
              )}
              <MessageBubble
                message={m}
                tail={firstOfRun}
                quoted={m.reply_to_wamid ? byWamid.get(m.reply_to_wamid) : undefined}
                onReply={canSend && m.direction !== "NOTE" ? () => (setReplyTo(m), setNoteMode(false), inputRef.current?.focus()) : undefined}
                onCopy={() => navigator.clipboard.writeText(messageText(m)).then(() => success("Copied"))}
              />
            </React.Fragment>
          );
        })}
      </div>

      {/* Input bar */}
      {canSend && conv.conversation_status === "OPEN" && (
        <div style={{ background: "var(--wa-header)" }} className="shrink-0">
          {noteMode && (
            <div className="flex items-center gap-2 px-4 pt-2 text-[12.5px]" style={{ color: "var(--wa-system-text)" }}>
              <StickyNote className="size-3.5" />
              <span className="flex-1">Internal note — only your team sees this, it is not sent to the buyer.</span>
              <button type="button" onClick={() => setNoteMode(false)} aria-label="Back to reply">
                <X className="size-4" />
              </button>
            </div>
          )}
          {replyTo && !noteMode && (
            <div className="px-4 pt-2">
              <div className="flex items-center gap-2 rounded-lg px-3 py-2" style={{ background: "var(--wa-panel)" }}>
                <div className="flex-1 min-w-0 border-l-4 pl-2" style={{ borderColor: replyTo.direction === "IN" ? "#a5b337" : "var(--wa-green)" }}>
                  <div className="text-[12.5px] font-medium" style={{ color: replyTo.direction === "IN" ? "#a5b337" : "var(--wa-green)" }}>
                    {replyTo.direction === "IN" ? conv.profile_name || conv.phone : "You"}
                  </div>
                  <div className="text-[13px] truncate" style={{ color: "var(--wa-muted)" }}>
                    {messageText(replyTo)}
                  </div>
                </div>
                <button type="button" onClick={() => setReplyTo(null)} aria-label="Cancel reply" style={{ color: "var(--wa-muted)" }}>
                  <X className="size-5" />
                </button>
              </div>
            </div>
          )}

          {replyLocked ? (
            <div className="px-4 py-3 flex flex-wrap items-center justify-center gap-3 text-[13px]" style={{ color: "var(--wa-muted)" }}>
              <LockKeyhole className="size-4" />
              <span>
                {conv.opted_out
                  ? "The buyer replied STOP. You can reply after they message you again."
                  : "More than 24 hours since the buyer's last message — only an approved template can be sent."}
              </span>
              {!conv.opted_out && (
                <button
                  type="button"
                  onClick={() => setTemplateOpen(true)}
                  className="px-4 py-1.5 rounded-full text-white text-[13px] font-medium"
                  style={{ background: "var(--wa-green)" }}
                >
                  Send template
                </button>
              )}
              <button type="button" onClick={() => setNoteMode(true)} className="underline">
                Add internal note
              </button>
            </div>
          ) : (
            <div className="relative flex items-end gap-2 px-3 py-2.5">
              {suggestions.length > 0 && (
                <div
                  className="absolute bottom-full left-3 right-3 mb-1 z-10 rounded-lg overflow-hidden"
                  style={{ background: "var(--wa-panel)", boxShadow: "0 2px 8px rgba(11,20,26,.2)" }}
                >
                  {suggestions.map((q) => (
                    <button
                      key={q.id}
                      type="button"
                      onClick={() => setText(q.body)}
                      className="w-full text-left px-4 py-2 text-[13.5px] hover:bg-[var(--wa-hover)]"
                      style={{ color: "var(--wa-text)" }}
                    >
                      <span className="font-semibold" style={{ color: "var(--wa-green)" }}>
                        /{q.shortcut}
                      </span>
                      <span style={{ color: "var(--wa-muted)" }}> {q.body.slice(0, 90)}</span>
                    </button>
                  ))}
                </div>
              )}

              <input ref={fileRef} type="file" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    title="Attach"
                    aria-label="Attach"
                    disabled={sending}
                    className="size-10 shrink-0 rounded-full flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/5"
                    style={{ color: "var(--wa-muted)" }}
                  >
                    <Plus className="size-6" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="top" className="w-56">
                  {!noteMode && (
                    <>
                      <DropdownMenuItem onClick={() => fileRef.current?.click()} className="gap-3 cursor-pointer">
                        <FileText className="size-4 text-[#7f66ff]" /> Document
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => fileRef.current?.click()} className="gap-3 cursor-pointer">
                        <ImageIcon className="size-4 text-[#007bfc]" /> Photos & videos
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setProductOpen(true)} className="gap-3 cursor-pointer">
                        <Package className="size-4 text-[#ff8a00]" /> Product card
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setTemplateOpen(true)} className="gap-3 cursor-pointer">
                        <LayoutTemplate className="size-4 text-[#02a698]" /> Template
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setButtonsOpen(true)} className="gap-3 cursor-pointer">
                        <ListChecks className="size-4 text-[#0063cb]" /> Reply buttons
                      </DropdownMenuItem>
                      {conv.deal_id && (
                        <DropdownMenuItem onClick={() => setQuoteOpen(true)} className="gap-3 cursor-pointer">
                          <Receipt className="size-4 text-[#ff2e74]" /> Quotation PDF
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={() => setNoteMode((v) => !v)} className="gap-3 cursor-pointer">
                    <StickyNote className="size-4 text-[#ffbc38]" /> {noteMode ? "Back to reply" : "Internal note"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <EmojiPicker onPick={insertEmoji} disabled={sending} />

              <div
                className="flex-1 min-w-0 rounded-lg px-3 py-[9px]"
                style={{ background: noteMode ? "var(--wa-note)" : "var(--wa-input)" }}
              >
                <textarea
                  ref={inputRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (suggestions.length === 1 && slash !== null) setText(suggestions[0]!.body);
                      else submitText();
                    }
                  }}
                  rows={Math.min(6, Math.max(1, text.split("\n").length))}
                  placeholder={noteMode ? "Write a note for your team" : "Type a message"}
                  className="w-full resize-none bg-transparent text-[15px] leading-5 outline-none block max-h-32 placeholder:text-[var(--wa-muted)]"
                  style={{ color: "var(--wa-text)" }}
                />
              </div>

              {!noteMode && !text.trim() ? (
                <VoiceRecorder
                  disabled={sending}
                  onError={(m) => error("Voice note", m)}
                  onSend={(file) => onFile(file, false)}
                />
              ) : (
              <button
                type="button"
                onClick={submitText}
                disabled={sending || !text.trim()}
                aria-label={noteMode ? "Save note" : "Send"}
                className="size-10 shrink-0 rounded-full flex items-center justify-center text-white disabled:opacity-60 transition-opacity"
                style={{ background: noteMode ? "#d4a017" : "var(--wa-green)" }}
              >
                {sending ? <Loader2 className="size-5 animate-spin" /> : noteMode ? <StickyNote className="size-5" /> : <SendHorizontal className="size-5" />}
              </button>
              )}
            </div>
          )}
          {!replyLocked && !noteMode && (
            <div className="px-4 pb-1.5 -mt-1 text-[11px]" style={{ color: "var(--wa-muted)" }}>
              {windowLabel(conv.window_closes_at, windowOpen)} · type / for quick replies
            </div>
          )}
        </div>
      )}
      {conv.conversation_status === "CLOSED" && (
        <div className="shrink-0 px-4 py-3 text-[13px] text-center" style={{ background: "var(--wa-header)", color: "var(--wa-muted)" }}>
          This conversation is closed. Reopen it to reply.
        </div>
      )}

      <ButtonsDialog
        open={buttonsOpen}
        onOpenChange={setButtonsOpen}
        sending={sending}
        onSend={async (body, buttons) => {
          if (await send({ kind: "buttons", text: body, buttons })) setButtonsOpen(false);
        }}
      />
      <TemplateDialog
        open={templateOpen}
        onOpenChange={setTemplateOpen}
        dealId={conv.deal_id}
        defaultName={detail.contact?.name || conv.profile_name || ""}
        onSend={async (payload) => {
          const ok = await send({ kind: "template", ...payload });
          if (ok) setTemplateOpen(false);
        }}
        sending={sending}
      />
      <ShareProductDialog
        open={productOpen}
        onOpenChange={setProductOpen}
        conversationId={conv.id}
        windowOpen={windowOpen}
        onSent={async () => {
          await load();
          onChanged?.();
        }}
      />
      {conv.deal_id && (
        <SendQuoteDialog
          open={quoteOpen}
          onOpenChange={setQuoteOpen}
          conversationId={conv.id}
          dealId={conv.deal_id}
          windowOpen={windowOpen}
          onSent={async () => {
            await load();
            onChanged?.();
          }}
        />
      )}
    </div>
  );
}

function MessageBubble({
  message: m,
  tail,
  quoted,
  onReply,
  onCopy,
}: {
  message: WaMessage;
  tail: boolean;
  quoted?: WaMessage;
  onReply?: () => void;
  onCopy: () => void;
}) {
  // System lines ("Assigned to Priya") as WhatsApp-style centred chips
  if (m.direction === "NOTE" && m.payload?.system) {
    return (
      <div className="flex justify-center my-1.5">
        <span
          className="text-[12.5px] px-3 py-1 rounded-lg"
          style={{ background: "var(--wa-chip)", color: "var(--wa-muted)", boxShadow: "var(--wa-shadow)" }}
        >
          {m.body} · {m.sent_by?.name} · {formatTime(m.message_at)}
        </span>
      </div>
    );
  }

  const incoming = m.direction === "IN";
  const note = m.direction === "NOTE";
  const mediaUrl = m.has_media ? `/api/WhatsApp/Media/${m.id}` : null;
  const isImage = (m.media_mime || "").startsWith("image/") && mediaUrl;

  if (m.message_type === "reaction") {
    return (
      <div className={cn("flex my-0.5", incoming ? "justify-start" : "justify-end")}>
        <span className="text-[12px] px-2 py-0.5 rounded-full" style={{ background: "var(--wa-chip)", color: "var(--wa-muted)", boxShadow: "var(--wa-shadow)" }}>
          {m.body || "—"} reacted · {formatTime(m.message_at)}
        </span>
      </div>
    );
  }

  const text = m.message_type === "template" || m.message_type === "text" || note ? m.body : m.caption || (m.has_media ? null : m.body);

  return (
    <div className={cn("group flex", incoming ? "justify-start" : "justify-end", tail ? "mt-2.5" : "mt-0.5")}>
      <div
        className={cn(
          "wa-bubble rounded-lg max-w-[88%] sm:max-w-[65%] text-[14.2px] leading-[19px]",
          incoming ? "wa-bubble-in" : note ? "wa-bubble-note" : "wa-bubble-out",
          tail && (incoming ? "wa-tail-in" : "wa-tail-out"),
          isImage ? "p-[3px]" : "px-[9px] pt-1.5 pb-2"
        )}
      >
        {/* Hover menu, like WhatsApp's chevron */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Message options"
              className="absolute top-1 right-1 z-[2] size-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-opacity"
              style={{ color: "var(--wa-muted)", background: incoming ? "var(--wa-in)" : note ? "var(--wa-note)" : "var(--wa-out)" }}
            >
              <ChevronDown className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            {onReply && (
              <DropdownMenuItem onClick={onReply} className="gap-2 cursor-pointer">
                <Reply className="size-4" /> Reply
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onCopy} className="gap-2 cursor-pointer">
              <Copy className="size-4" /> Copy
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className={cn(isImage && "px-[6px] pt-[3px]")}>
          {note && (
            <div className="flex items-center gap-1 text-[12.5px] font-medium mb-0.5" style={{ color: "#b7791f" }}>
              <StickyNote className="size-3" /> Internal note · {m.sent_by?.name}
            </div>
          )}
          {!incoming && !note && m.sent_by && tail && (
            <div className="text-[12.5px] font-medium mb-0.5" style={{ color: "var(--wa-green-dark)" }}>
              {m.sent_by.name}
            </div>
          )}
          {m.payload?.automatic && (
            <div className="text-[11.5px] mb-0.5 flex items-center gap-1" style={{ color: "var(--wa-muted)" }}>
              <Bot className="size-3" />
              {m.payload.automation === "after_hours"
                ? "Automatic after-hours reply"
                : m.payload.automation === "quote_expiry"
                  ? "Automatic quotation reminder"
                  : m.payload.automation === "quote_sent"
                    ? "Sent automatically with the quotation email"
                    : "Automatic message"}
            </div>
          )}
          {m.message_type === "template" && (
            <div className="text-[11.5px] mb-0.5 flex items-center gap-1" style={{ color: "var(--wa-muted)" }}>
              <LayoutTemplate className="size-3" /> Template · {m.template_name}
            </div>
          )}
          {quoted && (
            <div className="mb-1 rounded-md overflow-hidden flex" style={{ background: "var(--wa-quote)" }}>
              <div className="w-1 shrink-0" style={{ background: quoted.direction === "IN" ? "#a5b337" : "var(--wa-green)" }} />
              <div className="px-2 py-1 min-w-0">
                <div className="text-[12.5px] font-medium" style={{ color: quoted.direction === "IN" ? "#a5b337" : "var(--wa-green)" }}>
                  {quoted.direction === "IN" ? "Buyer" : "You"}
                </div>
                <div className="text-[13px] line-clamp-2" style={{ color: "var(--wa-muted)" }}>
                  {messageText(quoted)}
                </div>
              </div>
            </div>
          )}
        </div>

        <MessageMedia m={m} url={mediaUrl} />

        <div className={cn(isImage && "px-[6px] pb-[3px]")}>
          {m.message_type === "location" && m.payload?.latitude ? (
            <a
              href={`https://www.google.com/maps?q=${m.payload.latitude},${m.payload.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 underline"
              style={{ color: "#027eb5" }}
            >
              <MapPin className="size-4" /> {m.body || "Open location"}
            </a>
          ) : (
            text && (
              <span className={cn("whitespace-pre-wrap break-words", m.message_type === "unsupported" && "italic")} style={m.message_type === "unsupported" ? { color: "var(--wa-muted)" } : undefined}>
                {text}
              </span>
            )
          )}
          {/* Time + ticks float bottom-right, inside the bubble */}
          <span className="float-right ml-2.5 mt-1.5 -mb-1 flex items-center gap-1 text-[11px] leading-[15px] select-none" style={{ color: "var(--wa-muted)" }}>
            {formatTime(m.message_at)}
            {m.direction === "OUT" && <WaTicks status={m.message_status} />}
          </span>
          <span className="clear-both block" />
        </div>

        {m.direction === "OUT" && m.message_type === "interactive" && Array.isArray(m.payload?.buttons) && (
          <div className="-mx-[9px] -mb-2 mt-1.5 border-t" style={{ borderColor: "rgba(134,150,160,.25)" }}>
            {m.payload.buttons.map((b: string) => (
              <div key={b} className="text-center text-[14px] py-1.5 border-b last:border-b-0" style={{ color: "#027eb5", borderColor: "rgba(134,150,160,.25)" }}>
                {b}
              </div>
            ))}
          </div>
        )}
        {m.message_status === "FAILED" && m.error_message && (
          <div className="text-[12px] mt-1 px-0.5" style={{ color: "#ea0038" }}>
            {m.error_message}
          </div>
        )}
        {m.message_status === "RETRYING" && (
          <div className="text-[12px] mt-1 px-0.5" style={{ color: "var(--wa-muted)" }}>
            Temporary problem — retrying automatically
          </div>
        )}
      </div>
    </div>
  );
}

function MessageMedia({ m, url }: { m: WaMessage; url: string | null }) {
  const isMediaType = ["image", "sticker", "video", "audio", "document"].includes(m.message_type) || (m.message_type === "template" && url);
  if (!isMediaType) return null;
  if (!url) {
    return (
      <div className="text-[13px] italic mb-1" style={{ color: "var(--wa-muted)" }}>
        {m.payload?.media_removed
          ? "File removed after the retention period"
          : m.direction === "IN"
            ? "Media could not be downloaded"
            : "Attachment"}
      </div>
    );
  }
  const mime = m.media_mime || "";
  if (mime.startsWith("image/")) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="block">
        <img
          src={url}
          alt={m.file_name || "Image"}
          loading="lazy"
          className={cn("rounded-md object-cover max-h-80 min-w-[200px]", m.message_type === "sticker" && "max-h-32 min-w-0 object-contain")}
        />
      </a>
    );
  }
  if (mime.startsWith("video/")) return <video src={url} controls className="rounded-md max-h-80 mb-1" />;
  if (mime.startsWith("audio/")) return <audio src={url} controls className="mb-1 max-w-full h-10" />;
  const ext = (m.file_name?.split(".").pop() || "file").toUpperCase().slice(0, 4);
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-md px-3 py-2.5 mb-1 min-w-[240px]" style={{ background: "var(--wa-quote)" }}>
      <div className="w-8 h-10 rounded-sm flex items-end justify-center pb-1 text-[8px] font-bold text-white shrink-0" style={{ background: ext === "PDF" ? "#e2463d" : "#5f66cd" }}>
        {ext}
      </div>
      <div className="min-w-0">
        <div className="text-[14px] truncate" style={{ color: "var(--wa-text)" }}>
          {m.file_name || "Document"}
        </div>
        <div className="text-[11.5px]" style={{ color: "var(--wa-muted)" }}>
          {ext} {m.media_size ? `· ${m.media_size > 1048576 ? `${(m.media_size / 1048576).toFixed(1)} MB` : `${Math.ceil(m.media_size / 1024)} kB`}` : ""}
        </div>
      </div>
    </a>
  );
}
